import React, { useRef } from 'react'

import { Placement } from '@popperjs/core'
import cx from 'classnames'
import useOnClickOutside from 'conversations/hooks/useOnClickOutside'
import { usePopper } from 'react-popper'

import { Portal } from '@driftt/conversation-components'
import { ReactComponent as Arrow } from '@driftt/conversation-components/icons/arrow.svg'

import styles from './styles.module.css'
import utilityStyles from 'conversations/css/utility.module.css'

interface Props {
  active: boolean
  setActive: (active: boolean) => void
  menuContent: JSX.Element
  label: string
  offset?: any
  placement?: Placement
  'data-ocweb-id': string
}

const MenuRowItem: React.FC<{
  label: string
  onClick?: () => void
}> = ({ label, onClick }) => (
  <div onClick={onClick}>
    {label}
    <Arrow width="8" height="10" className={cx(utilityStyles.rotate270, styles.arrow)} />
  </div>
)

export const PopoverMenu: React.FC<Props> = ({
  active,
  setActive,
  menuContent,
  label,
  offset,
  placement,
  'data-ocweb-id': dataOcwebId,
}): JSX.Element | null => {
  const popperElem = useRef<HTMLDivElement | null>(null)
  const referenceElem = useRef<HTMLDivElement | null>(null)

  const {
    styles: popperStyles,
    attributes,
    update,
  } = usePopper(referenceElem.current, popperElem.current, {
    placement: placement ?? 'right-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: offset ?? [0, 8],
        },
      },
      {
        name: 'preventOverflow',
        options: { altBoundary: true },
      },
    ],
  })
  useOnClickOutside(popperElem, (e: MouseEvent) => {
    e.stopPropagation()
    setActive(false)
  })

  return (
    <>
      <div
        className={styles.rowTrigger}
        data-ocweb-id={dataOcwebId}
        onClick={(e) => {
          update && update()
          setActive(true)
          e.stopPropagation()
        }}>
        <div className={styles.menuItemContainer} ref={referenceElem}>
          <MenuRowItem label={label} />
        </div>
      </div>
      <Portal>
        <div
          ref={popperElem}
          className={cx(styles.menuContainer, { [styles.hiddenStyle]: !active })}
          style={popperStyles.popper}
          {...attributes.popper}>
          {active && menuContent}
        </div>
      </Portal>
    </>
  )
}
